/**
 * Collection page behaviour (sections/main-collection.liquid).
 *
 * <collection-facets>
 *   - AJAX filtering + sorting through the Section Rendering API
 *     (?section_id=...), with history.pushState and back/forward support.
 *   - Swaps only the parts that change (results, counts, filter values) so
 *     open filter groups, scroll position and focus are kept.
 *   - Mobile/tablet filter drawer: role="dialog", focus trap, Esc, backdrop,
 *     scroll lock, focus returned to the opener.
 *   - Desktop horizontal chips: one dropdown open at a time, Esc + outside click.
 *   - "Load more" appends the next page in place; numbered pagination is AJAX.
 * <price-range>: dual range slider kept in sync with the min/max inputs.
 */
(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const cache = new Map();
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const debounce = (fn, wait) => {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  };

  const fetchSection = async (url, sectionId, signal) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const key = target.toString();
    if (!cache.has(key)) {
      const response = await fetch(key, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      cache.set(key, await response.text());
    }
    return new DOMParser().parseFromString(cache.get(key), 'text/html');
  };

  const initMotion = (scope) => {
    if (window.theme && typeof window.theme.initMotion === 'function') window.theme.initMotion(scope);
  };

  if (!customElements.get('collection-facets')) {
    customElements.define(
      'collection-facets',
      class CollectionFacets extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.form = this.querySelector('[data-facet-form]');
          this.drawer = this.querySelector('[data-facets-drawer]');
          this.opener = this.querySelector('[data-facets-open]');
          this.status = this.querySelector('[data-facets-status]');
          this.currentSearch = window.location.search;
          this.requestId = 0;

          this.debouncedSubmit = debounce(() => this.submitForm(), 650);

          this.addEventListener('change', (event) => this.onChange(event));
          this.addEventListener('input', (event) => this.onInput(event));
          this.addEventListener('click', (event) => this.onClick(event));
          this.addEventListener('keydown', (event) => this.onKeydown(event));
          this.addEventListener('submit', (event) => {
            if (event.target !== this.form) return;
            event.preventDefault();
            this.submitForm();
          });

          this.onPopState = () => {
            if (window.location.search === this.currentSearch) return;
            this.render(window.location.href, { push: false });
          };
          window.addEventListener('popstate', this.onPopState);

          this.onMediaChange = () => {
            if (desktopQuery.matches) this.closeDrawer({ restoreFocus: false });
          };
          desktopQuery.addEventListener('change', this.onMediaChange);

          this.onDocumentClick = (event) => {
            if (!this.isHorizontalDesktop()) return;
            this.querySelectorAll('.facet[open]').forEach((details) => {
              if (!details.contains(event.target)) details.open = false;
            });
          };
          document.addEventListener('click', this.onDocumentClick);

          this.querySelectorAll('.facet').forEach((details) => {
            details.addEventListener('toggle', () => {
              if (!details.open || !this.isHorizontalDesktop()) return;
              this.querySelectorAll('.facet[open]').forEach((other) => {
                if (other !== details) other.open = false;
              });
            });
          });
        }

        disconnectedCallback() {
          window.removeEventListener('popstate', this.onPopState);
          desktopQuery.removeEventListener('change', this.onMediaChange);
          document.removeEventListener('click', this.onDocumentClick);
          document.documentElement.classList.remove('is-scroll-locked');
        }

        isHorizontalDesktop() {
          return desktopQuery.matches && Boolean(this.querySelector('.collection__body--horizontal'));
        }

        /* ---------- Events ---------- */
        onChange(event) {
          const target = event.target;
          if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
          if (target.form !== this.form && !target.closest('price-range')) return;
          this.debouncedSubmit.cancel();
          this.submitForm();
        }

        onInput(event) {
          if (event.target instanceof HTMLInputElement && event.target.type === 'number') this.debouncedSubmit();
        }

        onClick(event) {
          const link = event.target.closest('[data-facet-link], .pagination a');
          if (link && this.contains(link)) {
            event.preventDefault();
            const isPage = Boolean(link.closest('.pagination'));
            this.render(link.href, { scroll: isPage });
            return;
          }

          const more = event.target.closest('[data-load-more]');
          if (more) {
            event.preventDefault();
            this.loadMore(more);
            return;
          }

          if (event.target.closest('[data-facets-open]')) this.openDrawer();
          else if (event.target.closest('[data-facets-close]')) this.closeDrawer();
        }

        onKeydown(event) {
          if (this.drawer && this.drawer.classList.contains('is-open')) {
            if (event.key === 'Escape') {
              event.preventDefault();
              this.closeDrawer();
            } else if (event.key === 'Tab') {
              this.trapFocus(event);
            }
            return;
          }
          if (event.key === 'Escape' && this.isHorizontalDesktop()) {
            const open = event.target.closest('.facet[open]');
            if (!open) return;
            open.open = false;
            open.querySelector('summary').focus();
          }
        }

        /* ---------- Drawer ---------- */
        openDrawer() {
          if (!this.drawer || desktopQuery.matches) return;
          this.drawer.setAttribute('role', 'dialog');
          this.drawer.setAttribute('aria-modal', 'true');
          this.drawer.classList.add('is-open');
          if (this.opener) this.opener.setAttribute('aria-expanded', 'true');
          document.documentElement.classList.add('is-scroll-locked');
          const close = this.drawer.querySelector('[data-facets-close]');
          requestAnimationFrame(() => close && close.focus());
        }

        closeDrawer({ restoreFocus = true } = {}) {
          if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
          this.drawer.classList.remove('is-open');
          this.drawer.removeAttribute('role');
          this.drawer.removeAttribute('aria-modal');
          if (this.opener) this.opener.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('is-scroll-locked');
          if (restoreFocus && this.opener) this.opener.focus();
        }

        trapFocus(event) {
          const items = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
            (el) => el.getClientRects().length > 0 && !el.closest('details:not([open]) > :not(summary)')
          );
          if (items.length === 0) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (!this.drawer.contains(document.activeElement)) {
            event.preventDefault();
            first.focus();
          } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        /* ---------- Rendering ---------- */
        submitForm() {
          if (!this.form) return;
          const params = new URLSearchParams();
          new FormData(this.form).forEach((value, key) => {
            if (typeof value === 'string' && value.trim() !== '') params.append(key, value);
          });
          const query = params.toString();
          this.render(`${window.location.pathname}${query ? `?${query}` : ''}`);
        }

        setLoading(loading) {
          this.classList.toggle('is-loading', loading);
          const results = this.querySelector('[data-facets-results]');
          if (results) results.setAttribute('aria-busy', String(loading));
        }

        async render(url, { push = true, scroll = false } = {}) {
          const target = new URL(url, window.location.origin);
          target.searchParams.delete('section_id');
          if (this.controller) this.controller.abort();
          this.controller = new AbortController();
          const requestId = ++this.requestId;
          this.setLoading(true);

          try {
            const doc = await fetchSection(target, this.sectionId, this.controller.signal);
            if (requestId !== this.requestId) return;
            this.swap(doc);
            const sort = this.querySelector('select[name="sort_by"]');
            const nextSort = doc.querySelector('select[name="sort_by"]');
            if (sort && nextSort) sort.value = nextSort.value;
            this.currentSearch = target.search;
            if (push) window.history.pushState({ facets: true }, '', `${target.pathname}${target.search}`);
            if (scroll) {
              const results = this.querySelector('[data-facets-results]');
              const reduce = window.theme && window.theme.motionOff && window.theme.motionOff();
              results.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
              results.focus({ preventScroll: true });
            }
          } catch (error) {
            if (error.name === 'AbortError') return;
            window.location.assign(target.toString());
          } finally {
            if (requestId === this.requestId) this.setLoading(false);
          }
        }

        swap(doc) {
          const active = document.activeElement;
          const focusId = active && this.contains(active) ? active.id : '';
          const focusWasInResults = active && active.closest && active.closest('[data-facets-results]');

          const replace = (selector) => {
            const next = doc.querySelector(selector);
            const current = this.querySelector(selector);
            if (next && current) current.replaceWith(next);
            return next;
          };

          const results = replace('[data-facets-results]');
          const sid = this.sectionId;
          replace(`#ResultCount-${sid}`);
          replace('[data-filter-count]');
          replace('[data-facets-show]');

          const clear = this.querySelector('[data-facets-clear]');
          const nextClear = doc.querySelector('[data-facets-clear]');
          if (clear && nextClear) clear.setAttribute('href', nextClear.getAttribute('href'));

          doc.querySelectorAll('[data-facet-count]').forEach((next) => {
            const current = this.querySelector(`[data-facet-count="${next.dataset.facetCount}"]`);
            if (current) current.replaceWith(next);
          });

          doc.querySelectorAll('[data-facet-panel]').forEach((next) => {
            const current = this.querySelector(`[data-facet-panel="${next.dataset.facetPanel}"]`);
            if (!current) return;
            // Leave the panel the shopper is typing in alone (price inputs).
            if (current.contains(document.activeElement) && current.querySelector('price-range')) return;
            current.replaceWith(next);
          });

          if (focusId && !document.getElementById(focusId)) {
            if (results) results.focus({ preventScroll: true });
          } else if (focusId) {
            const el = document.getElementById(focusId);
            if (el !== document.activeElement) el.focus({ preventScroll: true });
          } else if (focusWasInResults && results) {
            results.focus({ preventScroll: true });
          }

          if (results) initMotion(results);
          const count = doc.getElementById(`ResultCount-${sid}`);
          if (this.status && count) this.status.textContent = count.textContent.trim();
        }

        async loadMore(button) {
          if (button.getAttribute('aria-busy') === 'true') return;
          button.setAttribute('aria-busy', 'true');
          try {
            const doc = await fetchSection(button.href, this.sectionId);
            const grid = this.querySelector('[data-product-grid]');
            const items = Array.from(doc.querySelectorAll('[data-product-grid] > li'));
            items.forEach((item) => grid.append(item));

            const wrap = this.querySelector('[data-load-more-wrap]');
            const nextWrap = doc.querySelector('[data-load-more-wrap]');
            if (wrap && nextWrap) wrap.replaceWith(nextWrap);

            initMotion(grid);
            const firstLink = items[0] && items[0].querySelector('[data-card-link]');
            if (firstLink) firstLink.focus({ preventScroll: true });
            const status = nextWrap && nextWrap.querySelector('.load-more__status');
            if (this.status && status) this.status.textContent = status.textContent.trim();
          } catch (error) {
            window.location.assign(button.href);
          }
        }
      }
    );
  }

  if (!customElements.get('price-range')) {
    customElements.define(
      'price-range',
      class PriceRange extends HTMLElement {
        connectedCallback() {
          this.slider = this.querySelector('.price-range__slider');
          this.minRange = this.querySelector('[data-range-min]');
          this.maxRange = this.querySelector('[data-range-max]');
          this.minInput = this.querySelector('[data-price-min]');
          this.maxInput = this.querySelector('[data-price-max]');
          this.max = Number(this.dataset.max) || 0;
          if (!this.minRange || !this.maxRange || !this.minInput || !this.maxInput) return;

          [this.minRange, this.maxRange].forEach((range) =>
            range.addEventListener('input', () => this.fromRange(range))
          );
          [this.minInput, this.maxInput].forEach((input) => input.addEventListener('input', () => this.fromInputs()));
          this.paint();
        }

        fromRange(range) {
          let min = Number(this.minRange.value);
          let max = Number(this.maxRange.value);
          if (min > max) {
            if (range === this.minRange) min = max;
            else max = min;
            this.minRange.value = min;
            this.maxRange.value = max;
          }
          this.minInput.value = min > 0 ? min : '';
          this.maxInput.value = max < this.max ? max : '';
          this.paint();
        }

        fromInputs() {
          this.minRange.value = this.minInput.value === '' ? 0 : this.minInput.value;
          this.maxRange.value = this.maxInput.value === '' ? this.max : this.maxInput.value;
          this.paint();
        }

        paint() {
          if (!this.max) return;
          const min = Math.min(Number(this.minRange.value) / this.max, 1);
          const max = Math.min(Number(this.maxRange.value) / this.max, 1);
          this.slider.style.setProperty('--min', min.toFixed(4));
          this.slider.style.setProperty('--max', Math.max(max, min).toFixed(4));
        }
      }
    );
  }
})();
